import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { isEmpty } from 'lodash/lang';
import { omit } from 'lodash/object';
import moment from 'moment';

import { FinInvoiceApi, FinLedgerEventApi } from '../../../../api';
import useSessionStorage from '../../../../common/hooks/useSessionStorage';
import NotificationManager from '../../../../common/notifications/NotificationManager';
import {
  EXPORT_FAILED,
  INVOICE_MAX_ALLOWABLE_RECORDS_ERROR,
  SOMETHING_WENT_WRONG
} from '../../../../constants/notificationMessages';
import { handleFinLedgerEventIdFieldValidationError, onFileSave, onRequestError } from '../../../../services/handlers';
import { studyTypeOptions } from '../shared/FinanceFilters/MultiSelectStudyType';

import { resolveEventSuffix } from './CreateInvoice/CreateInvoiceServices';
import {
  CREDIT_MEMO,
  DEBIT_MEMO,
  INVOICE_FILTERS,
  INVOICE_SELECTED_SEARCH_RESULT,
  PARENT
} from './InvoiceTable/TableConstants';
import { ACTIVE, INVOICED, PENDING, UNKNOWN } from './invoceConstants';

export const InvoiceContext = React.createContext(null);

export function InvoiceProvider({ children }) {
  const [savedFilters, setSavedFilters] = useSessionStorage(INVOICE_FILTERS, {});
  const [includeAdjustments, setIncludeAdjustments] = useState(true);
  const [savedSelectedSearchResult, setSavedSelectedSearchResult] = useSessionStorage(
    INVOICE_SELECTED_SEARCH_RESULT,
    null
  );
  const emptyFilters = useMemo(
    () => ({
      studyName: null,
      siteName: null,
      siteId: null,
      studyId: null,
      finStudyTypes: studyTypeOptions,
      projectCode: null,
      statuses: [ACTIVE, INVOICED],
      startDate: moment()
        .subtract(1, 'year')
        .set({ hour: 0, minute: 0, second: 0, millisecond: 1 }),
      endDate: moment().set({ hour: 23, minute: 59, second: 59, millisecond: 0 }),
      subjectId: null,
      finLedgerEventId: null,
      invoiceNumber: null
    }),
    []
  );
  const initialFilter = useMemo(
    () =>
      !isEmpty(savedFilters)
        ? {
            ...savedFilters,
            startDate: moment(savedFilters.startDate),
            endDate: moment(savedFilters.endDate)
          }
        : { ...emptyFilters },
    [emptyFilters, savedFilters]
  );

  const [rowSelectionModel, setRowSelectionModel] = useState({
    type: 'include',
    ids: new Set([])
  });
  const selectedEventIds = useMemo(() => {
    return Array.from(rowSelectionModel.ids);
  }, [rowSelectionModel]);

  const [filterProperty, setFilterProperty] = useState(initialFilter);
  const [ledgerEvents, setLedgerEvents] = useState([]);

  const [appliedFilters, setAppliedFilters] = useState(null);
  const [selectedSearchResult, setSelectedSearchResult] = useState(savedSelectedSearchResult);

  const [tableLoading, setTableLoading] = useState(false);
  const [firstLoading, setFirstLoading] = useState(true);

  const [createInvoiceItems, setCreateInvoiceItems] = useState({});

  const [automaticallyGeneratedInvoice, setAutomaticallyGeneratedInvoice] = useState(null);

  const [filteredStudyTypes, setFilteredStudyTypes] = useState(() => {
    return savedFilters?.finStudyTypes || filterProperty.finStudyTypes || studyTypeOptions;
  });
  const [initialStudyTypes, setInitialStudyTypes] = useState(() => {
    return savedFilters?.finStudyTypes || filterProperty.finStudyTypes || studyTypeOptions;
  });

  const [editPDFMode, setEditPDFMode] = useState(false);
  const [invoiceDate, setInvoiceDate] = useState(null);

  const [invoiceCreationDate, setInvoiceCreationDate] = useState(null);
  const [isInvalidInvoiceDate, setIsInvalidInvoiceDate] = useState(false);

  const modifyFilters = useCallback(filterProperty => {
    let { statuses, ...filtersWithoutStatuses } = filterProperty;
    if (isEmpty(statuses)) {
      statuses.push(UNKNOWN);
    } else {
      if (statuses.includes(ACTIVE)) {
        statuses = statuses.map(status => {
          if (status === ACTIVE) {
            return PENDING;
          }
          return status;
        });
      }
    }
    return {
      statuses,
      ...filtersWithoutStatuses
    };
  }, []);

  const setFewFilterProperty = useCallback(
    function(filters) {
      setFilterProperty(prevFilters => ({
        ...prevFilters,
        ...filters,
        finStudyTypes: filteredStudyTypes
      }));
    },
    [filteredStudyTypes]
  );

  const resolveApiForTableData = useCallback((selectedSearchResult, filters) => {
    if (!isEmpty(selectedSearchResult)) {
      return FinInvoiceApi.getAllInvoicesByFilter({ ...filters, ledgerEventUniqueId: selectedSearchResult.id });
    } else {
      return FinInvoiceApi.getAllInvoicesByFilter(filters);
    }
  }, []);

  const updateTable = useCallback(
    (selectedSearchResult, filters) => {
      return resolveApiForTableData(selectedSearchResult, filters)
        .then(({ data }) => {
          if (data === '') {
            NotificationManager.error(INVOICE_MAX_ALLOWABLE_RECORDS_ERROR);
            return;
          }

          setLedgerEvents(
            data.map(ledgerEvent => ({
              ...ledgerEvent,
              tableId: resolveEventSuffix(ledgerEvent.eventId, ledgerEvent.invoiceType)
            }))
          );
          setFirstLoading(false);
        })
        .catch(error => {
          handleFinLedgerEventIdFieldValidationError(error);
        })
        .finally(() => setTableLoading(false));
    },
    [resolveApiForTableData]
  );

  const getDataForCreateInvoice = useCallback(
    eventsForCreateInvoice => {
      const normalizedEventsForGetData = ledgerEvents
        .filter(legerEvent => eventsForCreateInvoice.includes(legerEvent.tableId))
        .map(item => ({ eventId: item.eventId, invoiceType: item.invoiceType }));
      FinInvoiceApi.getDataForCreateInvoice(normalizedEventsForGetData).then(({ data }) => {
        setCreateInvoiceItems({
          originalLedgerEvents: data.originalLedgerEvents,
          withholdingLedgerEvents: data.withholdingLedgerEvents,
          creditMemoLedgerEvents: data.creditMemoLedgerEvents,
          debitMemoLedgerEvents: data.debitMemoLedgerEvents,
          budgetInvoiceDetails: data.budgetInvoiceDetails
        });
      });
    },
    [ledgerEvents]
  );

  const loadInvoice = useCallback(
    async (invoiceNumber, invoiceFileId, invoiceType, options = {}) => {
      const openInEditMode = typeof options === 'boolean' ? options : Boolean(options?.openInEditMode);
      if (invoiceNumber) {
        setIncludeAdjustments(true);
        setEditPDFMode(openInEditMode);
        try {
          const params = openInEditMode
            ? { includeLatestAdjustments: true }
            : {
                includeLatestAdjustments: false,
                invoiceFileId
              };
          const { data } = await FinLedgerEventApi.getCreatedInvoice(invoiceNumber, invoiceType, params);
          const createdInvoiceEvents = data.createdInvoiceEvents ?? [];
          const parentAdjustmentEventsFallback = createdInvoiceEvents
            .filter(event => event.invoiceType === PARENT && event.adjustmentSequence > 0)
            .map(event => ({
              ...event,
              invoiceType: event.invoiceAmount < 0 ? CREDIT_MEMO : DEBIT_MEMO
            }));
          const mergeEvents = (primaryEvents = [], fallbackEvents = []) => {
            const mergedEvents = [...(primaryEvents ?? []), ...(fallbackEvents ?? [])];
            const map = new Map();

            mergedEvents.forEach(event => {
              const eventKey = event.id ?? `${event.eventNumber}-${event.adjustmentSequence}-${event.invoiceType}`;
              if (!map.has(eventKey)) {
                map.set(eventKey, event);
              }
            });

            return Array.from(map.values());
          };
          const normalizedCreditMemoLedgerEvents = mergeEvents(
            data.creditMemoLedgerEvents,
            parentAdjustmentEventsFallback.filter(event => event.invoiceType === CREDIT_MEMO)
          );
          const normalizedDebitMemoLedgerEvents = mergeEvents(
            data.debitMemoLedgerEvents,
            parentAdjustmentEventsFallback.filter(event => event.invoiceType === DEBIT_MEMO)
          );
          const normalizedOriginalLedgerEvents = createdInvoiceEvents.filter(
            event => !(event.invoiceType === PARENT && event.adjustmentSequence > 0)
          );
          const shouldLoadInvoiceFile = invoiceFileId && (!openInEditMode || isEmpty(createInvoiceItems?.pdf));
          let fileName = openInEditMode ? createInvoiceItems?.fileName ?? null : null;
          let pdf = openInEditMode ? createInvoiceItems?.pdf ?? null : null;
          if (shouldLoadInvoiceFile) {
            const {
              data: { content, fileName: loadedFileName }
            } = await FinInvoiceApi.getInvoiceFile(invoiceFileId);
            fileName = loadedFileName;
            pdf = `data:application/pdf;base64,${content}`;
          }
          setCreateInvoiceItems({
            originalLedgerEvents: normalizedOriginalLedgerEvents,
            creditMemoLedgerEvents: normalizedCreditMemoLedgerEvents,
            debitMemoLedgerEvents: normalizedDebitMemoLedgerEvents,
            createdInvoiceDetails: {
              ...data.createdInvoiceDetails,
              invoiceNumber,
              invoiceType
            },
            pdf,
            fileName,
            invoiceFileId: invoiceFileId ?? createInvoiceItems?.invoiceFileId ?? null
          });
          setInvoiceDate(
            data?.createdInvoiceDetails?.invoiceDate ? moment(data?.createdInvoiceDetails?.invoiceDate) : null
          );
          setInvoiceCreationDate(
            data?.createdInvoiceDetails?.invoiceCreationDate
              ? moment(data?.createdInvoiceDetails?.invoiceCreationDate)
              : null
          );
        } catch {
          NotificationManager.error(SOMETHING_WENT_WRONG);
        }
      }
    },
    [
      createInvoiceItems?.fileName,
      createInvoiceItems?.invoiceFileId,
      createInvoiceItems?.pdf,
      setCreateInvoiceItems,
      setIncludeAdjustments,
      setEditPDFMode
    ]
  );

  const onEditPDF = useCallback(() => {
    const invoiceNumber = createInvoiceItems?.createdInvoiceDetails?.invoiceNumber;
    const invoiceType = createInvoiceItems?.createdInvoiceDetails?.invoiceType;
    const invoiceFileId = createInvoiceItems?.invoiceFileId;

    if (invoiceNumber && invoiceType) {
      loadInvoice(invoiceNumber, invoiceFileId, invoiceType, { openInEditMode: true });
    }
  }, [
    createInvoiceItems?.createdInvoiceDetails?.invoiceNumber,
    createInvoiceItems?.createdInvoiceDetails?.invoiceType,
    createInvoiceItems?.invoiceFileId,
    loadInvoice
  ]);

  const getDataForAutomaticallyCreatedInvoice = useCallback(
    async (invoiceNumber, invoiceType, invoiceFileId, openInEditMode = false) => {
      try {
        const { data } = await FinLedgerEventApi.getCreatedInvoice(invoiceNumber, invoiceType);
        const client = isEmpty(data.client) ? JSON.parse(data.createdInvoiceDetails.client) : data.client;
        setAutomaticallyGeneratedInvoice({
          createdInvoiceDetails: { ...data.createdInvoiceDetails, client },
          createdInvoiceEvents: data.createdInvoiceEvents,
          invoiceFileId,
          openInEditMode
        });
      } catch {
        NotificationManager.error(SOMETHING_WENT_WRONG);
      }
    },
    []
  );

  const applyFilters = useCallback(() => {
    setTableLoading(true);
    setSavedFilters(filterProperty);
    const filters = modifyFilters(filterProperty);
    updateTable(selectedSearchResult, filters).then(() => {
      setAppliedFilters({ ...filterProperty });
    });
  }, [filterProperty, modifyFilters, selectedSearchResult, setSavedFilters, updateTable]);

  const resetFilters = useCallback(() => {
    setFilterProperty(emptyFilters);
    setSelectedSearchResult(null);
    setLedgerEvents([]);
    setAppliedFilters(null);
    setFirstLoading(true);
    setSavedSelectedSearchResult(null);
    setSavedFilters(null);
    setFilteredStudyTypes(studyTypeOptions);
    setInitialStudyTypes(studyTypeOptions);
  }, [emptyFilters, setSavedFilters, setSavedSelectedSearchResult]);

  const modifyFiltersForNFExport = useCallback(filterProperty => {
    let { statuses, ...properties } = filterProperty;
    if (isEmpty(statuses)) {
      statuses.push(UNKNOWN);
    } else {
      if (statuses.includes(ACTIVE)) {
        statuses = statuses.map(status => {
          if (status === ACTIVE) {
            return PENDING;
          }
          return status;
        });
      }
    }

    const studyId = filterProperty.studyId || null;
    const siteId = filterProperty.siteId || null;

    const studyName = filterProperty.studyName || null;
    const siteName = filterProperty.siteName || null;
    const projectCode = filterProperty.projectCode || null;

    return {
      statuses,
      studyId,
      siteId,
      studyName,
      siteName,
      projectCode,
      ...omit(properties, ['studyIds', 'siteIds', 'studyName', 'siteName', 'projectCode', 'pcnIds', 'types'])
    };
  }, []);

  useEffect(() => {
    setSelectedSearchResult(null);
  }, [
    filterProperty.studyName,
    filterProperty.siteName,
    filterProperty.siteId,
    filterProperty.studyId,
    filterProperty.projectCode,
    filterProperty.statuses,
    filterProperty.startDate,
    filterProperty.subjectId,
    filterProperty.finLedgerEventId,
    filterProperty.invoiceNumber
  ]);

  useEffect(() => {
    setSavedSelectedSearchResult(selectedSearchResult);
  }, [selectedSearchResult, setSavedSelectedSearchResult]);

  useEffect(() => {
    if (!isEmpty(savedFilters)) {
      applyFilters();
    }
    // eslint-disable-next-line
  }, []);
  const downloadCSV = useCallback(() => {
    let filters = modifyFilters(appliedFilters);
    if (ledgerEvents.length === 1) {
      filters = { ...filters, ledgerEventUniqueId: ledgerEvents[0]?.id };
    }
    FinInvoiceApi.exportInvoices(filters)
      .then(onFileSave)
      .catch(err => onRequestError(err, { customMessage: EXPORT_FAILED }));
  }, [appliedFilters, ledgerEvents, modifyFilters]);

  const downloadNSCSV = useCallback(() => {
    const filters = modifyFiltersForNFExport(filterProperty);
    FinInvoiceApi.exportNS(filters)
      .then(onFileSave)
      .catch(err => onRequestError(err, { customMessage: EXPORT_FAILED }));
  }, [filterProperty, modifyFiltersForNFExport]);

  const value = useMemo(
    () => ({
      downloadCSV,
      applyFilters,
      resetFilters,
      modifyFilters,
      filterProperty,
      tableLoading,
      setFewFilterProperty,
      setSelectedSearchResult,
      ledgerEvents,
      appliedFilters,
      firstLoading,
      downloadNSCSV,
      createInvoiceItems,
      setCreateInvoiceItems,
      selectedEventIds,
      rowSelectionModel,
      setRowSelectionModel,
      getDataForCreateInvoice,
      loadInvoice,
      getDataForAutomaticallyCreatedInvoice,
      automaticallyGeneratedInvoice,
      setAutomaticallyGeneratedInvoice,
      editPDFMode,
      setEditPDFMode,
      onEditPDF,
      filteredStudyTypes,
      setFilteredStudyTypes,
      initialStudyTypes,
      setInitialStudyTypes,
      invoiceDate,
      setInvoiceDate,
      invoiceCreationDate,
      isInvalidInvoiceDate,
      setIsInvalidInvoiceDate,
      setIncludeAdjustments,
      includeAdjustments
    }),
    [
      downloadCSV,
      applyFilters,
      resetFilters,
      modifyFilters,
      filterProperty,
      tableLoading,
      setFewFilterProperty,
      ledgerEvents,
      appliedFilters,
      firstLoading,
      downloadNSCSV,
      createInvoiceItems,
      selectedEventIds,
      rowSelectionModel,
      getDataForCreateInvoice,
      loadInvoice,
      getDataForAutomaticallyCreatedInvoice,
      automaticallyGeneratedInvoice,
      editPDFMode,
      onEditPDF,
      filteredStudyTypes,
      initialStudyTypes,
      invoiceDate,
      setInvoiceDate,
      invoiceCreationDate,
      isInvalidInvoiceDate,
      setIsInvalidInvoiceDate,
      setIncludeAdjustments,
      includeAdjustments
    ]
  );

  return <InvoiceContext.Provider value={value}>{children}</InvoiceContext.Provider>;
}

export function withNewInvoiceContext(Component) {
  return function WrapperComponent(props) {
    return (
      <InvoiceProvider>
        <Component {...props} />
      </InvoiceProvider>
    );
  };
}
